import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { Cohort } from 'TenantModule/api/getCohortsApi';
import {
  CreateCohortPayload,
  UpdateCohortPayload,
  createCohortApi,
  updateCohortApi,
} from 'TenantModule/api/cohortAdminApi';
import { cohortQueryKeys } from './cohortQueryKeys';

/**
 * Cohort admin mutations (RDM-2031/GLX-1869). Both invalidate every cohort list so
 * the group-filling picker and the admin table reflect capacity/status edits
 * without a page reload.
 */
export const useCohortMutations = () => {
  const token = useSelector(selectAuthToken) as string | undefined;
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  // RETURNED from onSuccess so react-query keeps the mutation pending until the
  // cohort lists have refetched. Otherwise isPending flips false the moment the
  // write resolves, re-enabling the capacity steppers while they still render the
  // OLD capacityLimit — a second click then resends the same value and silently
  // drops the operator's increment.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: cohortQueryKeys.lists() });

  const createMutation = useMutation<AxiosResponse<Cohort>, unknown, CreateCohortPayload>({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return createCohortApi({ params, token });
    },
    onSuccess: () => {
      showSuccess('Cohort registered');
      return invalidate();
    },
    onError: (error) => {
      showError(`Failed to register cohort: ${extractApiErrorMessage(error)}`);
    },
  });

  const updateMutation = useMutation<
    AxiosResponse<Cohort>,
    unknown,
    { id: number; body: UpdateCohortPayload }
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return updateCohortApi({ params, token });
    },
    onSuccess: () => {
      showSuccess('Cohort updated');
      return invalidate();
    },
    onError: (error) => {
      showError(`Failed to update cohort: ${extractApiErrorMessage(error)}`);
    },
  });

  return {
    createCohort: createMutation.mutate,
    isCreatingCohort: createMutation.isPending,
    updateCohort: updateMutation.mutate,
    isUpdatingCohort: updateMutation.isPending,
  };
};
